import React, { useState, useRef, useEffect } from "react";
import Select, { SingleValue, createFilter } from "react-select";
import {
  CaretDownSolidIcon,
  CaretUpSolidIcon,
  DeleteIcon,
  EditIcon,
  FilterIcon,
  ResetIcon,
  TickIcon,
  UserAddIcon,
} from "../../../assets/icons";
import Pagination from "../../shared/common/pagination";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import { getStateAll } from "../../../api/subject.service";
import {
  deleteSchoolById,
  getMiniCRMDistricts,
  getMiniCRMSchools,
  updateSchool,
} from "../../../api/miniCrm.service";
import { IReactSelect } from "../../../model/interface/keyValuePair";
import { XIcon } from "@heroicons/react/outline";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../../shared/loader/loader";
import SchoolAdminModal from "./schoolAdmin";
import { useNavigate } from "react-router";
import constant from "../../../utils/constant";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../../src/utils/permissions";
import { debounce } from "lodash";
import { useLocation } from "react-router";

const Schools: React.FC = (props: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const defaultSelect = { label: "", value: 0 };
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [sortCol, setSortCol] = useState<string>("");
  const [sortByDesc, setSortByDesc] = useState<boolean>(false);
  const [schoolAdminModalOpen, setSchoolAdminModalOpen] =
    useState<boolean>(false);
  const [states, setStates] = useState<Array<any>>([]);
  const [selectedState, setSelectedStates] = useState<IReactSelect>();
  const [districts, setDistricts] = useState<Array<any>>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<Array<any>>([]);
  const defaultDistricts = [...districts]?.slice(0, 100);
  const [selectedDistrict, setSelectedDistrict] =
    useState<IReactSelect>(defaultSelect);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [schools, setSchools] = useState<Array<any>>([]);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRegion, setSelectedRegion] = useState<any>({
    label: "",
    value: "",
  });
  const [school, setSchool] = useState<string>("");
  const [editSchool, setEditSchool] = useState<any>();
  const [editFields, setEditFields] = useState<any>();
  const [deleteSchool, setDeleteSchool] = useState<any>();
  const [editSchoolName, setEditSchoolName] = useState<string>();
  const [editSelectedState, setEditSelectedStates] = useState<IReactSelect>();
  const [editSelectedDistrict, setEditSelectedDistrict] =
    useState<IReactSelect>(defaultSelect);
  const [editCity, setEditCity] = useState<string>();
  const [editZip, setEditZip] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [schoolName, setSchoolName] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [initialRender, setInitialRender] = useState(false);
  const [isFilterJustLoaded, setIsFilterJustLoaded] = useState(false);

  const filterSchools = () => {
    const { crmType }: { crmType?: string } =
      typeof state === "object" && state !== null ? state : {};

    if (crmType) {
      getSchools(false, true);
    }
  };

  const saveFilterState = (isReset?) => {
    if (isReset) {
      localStorage.removeItem("miniCRM_schoolFields");
    } else {
      const schoolFields = {
        schoolName: schoolName,
        selectedState: selectedState,
        selectedDistrict: selectedDistrict,
      };
      localStorage.setItem(
        "miniCRM_schoolFields",
        JSON.stringify(schoolFields)
      );
    }
  };

  const loadFilterState = () => {
    const savedFields = localStorage.getItem("miniCRM_schoolFields");
    if (savedFields) {
      const schoolFields = JSON.parse(savedFields);
      setSchoolName(schoolFields.schoolName);
      setSelectedStates(schoolFields.selectedState);
      setSelectedDistrict(schoolFields.selectedDistrict);
      setIsFilterJustLoaded(true);
    }
  };

  useEffect(() => {
    loadFilterState();
  }, []);

  useEffect(() => {
    if (isFilterJustLoaded) {
      getSchools();
      setIsFilterJustLoaded(false);
    }
  }, [isFilterJustLoaded]);

  useEffect(() => {
    if (
      (schoolName ||
        (selectedState && selectedState.value) ||
        (selectedDistrict && selectedDistrict.value)) &&
      !initialRender
    ) {
      filterSchools();
      setInitialRender(true);
    }
  }, [schoolName, selectedState, selectedDistrict]);

  useEffect(() => {
    if (isLoaded) {
      getSchools(false, false);
    }
  }, [pageNumber]);

  useEffect(() => {
    getStates();
    getDistricts();
    if (pageNumber === 1 && isLoaded) {
      getSchools(false, false);
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  useEffect(() => {
    if (isLoaded) {
      getSchools(false, false);
    }
  }, [sortByDesc, sortCol]);

  useEffect(() => {
    if (districts.length > 0) {
      setFilteredDistricts(defaultDistricts);
    }
  }, [districts]);

  const getStates = async () => {
    const resp: any = await getStateAll(9999, 1);
    if (resp && resp.data) {
      setStates(
        resp?.data?.data?.map((ds) => ({
          label: ds.name,
          value: ds.stateId,
        }))
      );
    }
  };

  const getSchools = async (isReset?, resetPageNumber?) => {
    setLoading(true);
    let params = {};
    if (isReset) {
      setFilteredDistricts(defaultDistricts);
      params = {
        stateId: 0,
        region: "",
        districtId: 0,
        districtName: "",
        schoolName: "",
        city: "",
        orderByColumn: sortCol,
        sortDir: sortByDesc ? "desc" : "asc",
        pageSize: 10,
        pageNumber: 1,
      };
    } else {
      params = {
        stateId: selectedState?.value,
        region: selectedRegion?.value,
        districtId: selectedDistrict?.value,
        schoolName: schoolName,
        city: city,
        orderByColumn: sortCol,
        sortDir: sortByDesc ? "desc" : "asc",
        pageSize: pageSize,
        pageNumber: resetPageNumber ? 1 : pageNumber,
      };
    }

    saveFilterState(isReset);

    const resp = await getMiniCRMSchools(params);

    if (resp && resp.data && resp.data.data) {
      setSchools(resp.data.data);
      setTotalPages(
        Math.ceil(resp.data.totalRecords / (isReset ? 10 : pageSize))
      );
      setTotalRecords(resp.data.totalRecords);
    }
    setLoading(false);
    setIsLoaded(true);
  };

  const handleSortBy = (column: string) => {
    if (sortCol === column) {
      setSortByDesc(!sortByDesc);
    } else {
      setSortByDesc(false);
      setSortCol(column);
    }
  };

  const openSchool = (schoolId) => {
    navigate(constant.SCHOOLDETAIL.replace(":id", schoolId));
  };

  const handleClickDistrictName = (districtId) => {
    navigate(constant.DISTRICTDETAIL.replace(":id", districtId));
  };

  const handleSchoolAdminModal = (school) => {
    setEditSchool(school);
    setSchoolAdminModalOpen(!schoolAdminModalOpen);
  };

  const onStateChange = async (e, type?) => {
    setLoading(true);

    const selectedElement = states.filter(
      (currentEle) => currentEle.value === e.value
    );
    setSelectedStates(selectedElement[0]);

    getDistricts(e.value);
  };

  const getDistricts = async (value = 0) => {
    const resp = await getMiniCRMDistricts({
      stateId: value,
      region: "",
      districtId: 0,
      districtName: "",
      orderByColumn: "",
      sortDir: "",
      pageSize: 9999,
      pageNumber: 1,
    });
    setLoading(false);
    if (resp && resp.data && resp.data.data) {
      setDistricts(
        resp.data.data.map((ds) => ({ label: ds.name, value: ds.districtId }))
      );
    }
  };

  const resetClickHandler = () => {
    setSchoolName("");
    setSelectedStates(defaultSelect);
    setSelectedRegion({ label: "", value: "" });
    setSelectedDistrict(defaultSelect);
    setSchool("");
    getSchools(true, true);
    setCity("");
    paginationRef?.current?.onReset();
    pageSizeRef?.current?.onReset();
    const form: any = document.getElementById("users_form");
    if (form) form.reset();
  };

  const onUpdate = async () => {
    setLoading(true);
    setEditFields({
      open: false,
      idToEdit: 0,
    });

    try {
      const resp = await updateSchool({
        schoolName: editSchoolName,
        stateName: editSelectedState?.label,
        stateId: editSelectedState?.value,
        districtName: editSelectedDistrict?.label,
        districtId: editSelectedState?.value,
        city: editCity,
        zip: editZip,
        updatedByUser: props?.profile?.userId,
      });
      setLoading(false);
      if (resp.data > 0) {
        toast.success("School updated successfully");
        getSchools();
      } else {
        toast.error("School update failed");
      }
    } catch (e) {
      toast.error("School update failed");
    }
  };

  const editModal = (school) => {
    setEditSchool(school);
    setEditSchoolName(school.name);
    setEditSelectedStates({
      label: school.stateName,
      value: school.stateId,
    });
    setEditSelectedDistrict({
      label: school.districtName,
      value: school.districtId,
    });
    setEditCity(school.city);
    setEditZip(school.zipCode);
  };

  const onDelete = async () => {
    setLoading(true);
    setDeleteSchool({ open: false, userId: 0, schoolName: "" });
    try {
      const resp = await deleteSchoolById({
        schoolId: deleteSchool?.userId,
        updatedByUser: props?.profile?.userId,
      });
      setLoading(false);
      if (resp.data > 0) {
        toast.success("User deleted successfully");
        getSchools();
      } else {
        toast.error("User delete failed");
      }
    } catch (e) {
      toast.error("User delete failed");
    }
  };

  const handleInputChange = (inputValue) => {
    if (inputValue === "") {
      setFilteredDistricts(defaultDistricts);
      return;
    }
    const tempDistricts = districts.filter((option) => {
      return option.label.toLowerCase().includes(inputValue.toLowerCase());
    });
    setFilteredDistricts(tempDistricts);
  };

  const debounceFilter = debounce(handleInputChange, 100);

  const onKeyPress = (e) => {
    if (e.charCode === 13) getSchools(false, true);
  };

  const handleSelectedDistrict = (option: SingleValue<IReactSelect> | null) => {
    if (option) {
      setSelectedDistrict(option);
    }
  };

  return (
    <form id="users_form" className="p-3" onKeyPress={onKeyPress}>
      {loading && <Loader />}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-3">
          <label className="text-sm text-gray-700">School</label>
          <input
            name="school"
            type="text"
            autoComplete="off"
            value={schoolName}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
            onChange={(e) => setSchoolName(e.target.value)}
          />
        </div>

        <div className="col-span-3">
          <label className="text-sm text-gray-700">State</label>
          <Select
            className="mt-1 text-sm"
            value={selectedState}
            options={states}
            onChange={(e) => onStateChange(e)}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: "#B3B3B3",
                primary: "#041E42",
              },
            })}
            styles={{
              control: (base, state) => ({
                ...base,
                background:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "#1A1C23"
                    : "white",
                borderColor:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "#6B7280"
                    : "#D1D5DB",
                borderWidth: "1px",
                "&:hover": {
                  borderColor:
                    state.isFocused &&
                    document.getElementsByClassName("dark")?.length > 0
                      ? "#6b7280"
                      : state.isFocused
                      ? "#4c5563"
                      : "",
                },
              }),
              singleValue: (base) => ({
                ...base,
                color:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "white"
                    : "#374051",
              }),
              input: (base) => ({
                ...base,
                color:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "white"
                    : "",
              }),
            }}
          />
        </div>

        <div className="col-span-3">
          <label className="text-sm text-gray-700">District</label>
          <Select
            className="mt-1 text-sm"
            value={selectedDistrict}
            options={filteredDistricts}
            onChange={handleSelectedDistrict}
            onInputChange={(value) => debounceFilter(value)}
            filterOption={createFilter({ ignoreAccents: false })}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: "#B3B3B3",
                primary: "#041E42",
              },
            })}
            styles={{
              control: (base, state) => ({
                ...base,
                background:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "#1A1C23"
                    : "white",
                borderColor:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "#6B7280"
                    : "#D1D5DB",
                borderWidth: "1px",
                "&:hover": {
                  borderColor:
                    state.isFocused &&
                    document.getElementsByClassName("dark")?.length > 0
                      ? "#6b7280"
                      : state.isFocused
                      ? "#4c5563"
                      : "",
                },
              }),
              singleValue: (base) => ({
                ...base,
                color:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "white"
                    : "#374051",
              }),
              input: (base) => ({
                ...base,
                color:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "white"
                    : "",
              }),
            }}
          />
        </div>

        <div className="col-span-3">
          <label className="text-sm text-gray-700">City</label>
          <input
            name="city"
            type="text"
            autoComplete="off"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
            onChange={(e) => setCity(e.target.value)}
          />
        </div>
      </div>

      <div className="flex justify-start mb-5">
        <button
          autoFocus
          type="button"
          onClick={() => getSchools(false, true)}
          className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
        >
          <FilterIcon className="w-3 h-3" />
          <span>Filter</span>
        </button>
        <button
          type="button"
          onClick={resetClickHandler}
          className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
        >
          <ResetIcon className="w-3 h-3" />
          <span>Reset Filter</span>
        </button>
      </div>

      <div id="dataContainer" className="mt-4 border-t pt-6">
        <div className="flex flex-col mt-1">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-primary-color">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider cursor-pointer"
                        onClick={() => handleSortBy("schoolName")}
                      >
                        <span className="flex">
                          School Name
                          {sortCol === "schoolName" && (
                            <span>
                              {!sortByDesc ? (
                                <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                              )}
                            </span>
                          )}
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider cursor-pointer"
                        onClick={() => handleSortBy("accountLoginName")}
                      >
                        <span className="flex">
                          Account Login Name
                          {sortCol === "accountLoginName" && (
                            <span>
                              {!sortByDesc ? (
                                <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                              )}
                            </span>
                          )}
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider cursor-pointer"
                        onClick={() => handleSortBy("district")}
                      >
                        <span className="flex">
                          District
                          {sortCol === "district" && (
                            <span>
                              {!sortByDesc ? (
                                <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                              )}
                            </span>
                          )}
                        </span>
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider cursor-pointer"
                        onClick={() => handleSortBy("city")}
                      >
                        <span className="flex">
                          City
                          {sortCol === "city" && (
                            <span>
                              {!sortByDesc ? (
                                <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                              )}
                            </span>
                          )}
                        </span>
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider cursor-pointer"
                        onClick={() => handleSortBy("state")}
                      >
                        <span className="flex">
                          State
                          {sortCol === "state" && (
                            <span>
                              {!sortByDesc ? (
                                <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                              )}
                            </span>
                          )}
                        </span>
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider cursor-pointer"
                      >
                        Zip
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Teacher Count
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Student Count
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Account Info
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Bundle
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Sellable Products
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        NWEA Id
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Enhanced Password Security
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Clever
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Class Link
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                      >
                        Canvas
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools?.map((school, i) => (
                      <tr
                        key={i}
                        className={`align-top ${
                          i % 2 === 0 ? "bg-white " : "bg-gray-100"
                        }`}
                      >
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          <button
                            className="text-indigo-500"
                            type="button"
                            onClick={() => {
                              openSchool(school.schoolId);
                            }}
                          >
                            {school.name}
                          </button>
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.accountLoginName}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          <button
                            className="text-indigo-500"
                            type="button"
                            onClick={() => {
                              handleClickDistrictName(school.districtId);
                            }}
                          >
                            {school.districtName}
                          </button>
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.city}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.stateName}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.zipCode}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.teacherCount}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.studentCount}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.accountInfo}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.bundle}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.sellableProduct}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.nweaId !==
                          "00000000-0000-0000-0000-000000000000"
                            ? school.nweaId
                            : ""}
                        </td>
                        <td className=" px-3 py-2 text-sm font-normal relative">
                          <div className="flex justify-center items-center">
                            {school.hasEnhancedPasswordSecurity && (
                              <TickIcon></TickIcon>
                            )}
                          </div>
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.cleverId}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.classLinkId}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {school.canvasAccountId}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          <div className="flex justify-center items-center">
                            <UserAddIcon
                              title="Add school admin"
                              onClick={() => {
                                setEditFields({
                                  open: true,
                                  idToEdit: school.schoolId,
                                });
                                handleSchoolAdminModal(school);
                              }}
                              className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                            />
                            <AllowedTo perform={Permissions.school_modify}>
                              <EditIcon
                                title="Edit school"
                                onClick={() => {
                                  setEditFields({
                                    open: true,
                                    idToEdit: school.schoolId,
                                  });
                                  openSchool(school.schoolId);
                                }}
                                className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                              />
                            </AllowedTo>
                            <AllowedTo perform={Permissions.school_delete}>
                              <DeleteIcon
                                title="Delete school"
                                onClick={() =>
                                  setDeleteSchool({
                                    open: true,
                                    schoolId: school.schoolId,
                                    schoolName: school.name,
                                  })
                                }
                                className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                              />
                            </AllowedTo>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {schools.length === 0 && (
                  <p className="p-2">No Records Found</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <Pagination
              pages={totalPages}
              setPageNumber={setPageNumber}
              ref={paginationRef}
            />
            <span>
              <span className="text-sm pr-4 ml-auto text-left-700">
                Showing {pageNumber} to{" "}
                {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                {totalRecords} results{" "}
              </span>

              <SelectPageSize
                setPageSize={setPageSize}
                type={"second"}
                ref={pageSizeRef}
              />
            </span>
          </div>
        </div>
      </div>

      {schoolAdminModalOpen && (
        <SchoolAdminModal
          handleSchoolAdminModal={handleSchoolAdminModal}
          school={editSchool}
        />
      )}

      {deleteSchool?.open && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="rounded-lg inline-block align-bottom bg-white px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 h-auto">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() =>
                    setDeleteSchool({ open: false, schoolName: "", userId: 0 })
                  }
                  type="button"
                  className="bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary"
                >
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="flex justify-center font-semibold">
                <h2 className="text-gray-700  text-2xl">Are you sure?</h2>
              </div>

              <div className="mt-2 flex justify-center">
                <h2 className="text-gray-700 text-lg">
                  You want to delete{" "}
                  <span className="font-semibold text-black text-xl">
                    {deleteSchool.schoolName}
                  </span>
                </h2>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  onClick={() => onDelete()}
                  className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                >
                  <span>Delete</span>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setDeleteSchool({ open: false, userId: 0, schoolName: "" })
                  }
                  className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                >
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

const mapStateToProps = (state: any) => {
  return {
    profile: state.profile,
  };
};
export default connect(mapStateToProps, {})(Schools);
